import {Component, computed} from '@angular/core';
import {Button} from 'primeng/button';
import {DatePipe} from '@angular/common';
import {ManageTaBaseComponent} from '../manage-ta-base-component.directive';
import {TaCostBubbleComponent} from '../../ta-cost-bubble/ta-cost-bubble.component';
import {ApprovalContext} from '../../../../api';
import {TaApprovalsTableComponent} from '../ta-approvals-table/ta-approvals-table.component';

/**
 * The manage page for a PendingCancelApproval TA - a pre-paid TA whose cancellation was requested
 * after its prepayment had been processed, so the cancellation itself needs approver sign-off
 * before the TA is zeroed and finance can recover the money. Permissions mirror
 * manage-ta-acquittal-approval: the holder of a pending Cancellation approval (or a global admin)
 * acts, everyone else sees the page read only.
 *
 * Approve is the only approval action - the decision to cancel has already been made, and the
 * server offers no withhold or reject for a cancellation (TAStore.ApproveCancellationSubmission).
 * Finance/admin can instead reverse the cancellation outright, which undoes the request rather than
 * deciding it (AdminController.RevertPrepayCancel).
 *
 * No itinerary is shown. Nothing has changed about the travel at this point - the zero-value
 * version does not exist until the cancellation is approved - so an itinerary comparison would only
 * render the unchanged original against itself. The itinerary belongs on manage-ta-cancel-finalise,
 * where there is a difference to see. This page is the cancellation decision alone, which is why it
 * also fetches nothing on init.
 */
@Component({
  selector: 'manage-ta-cancel-approval',
  imports: [
    Button,
    DatePipe,
    TaCostBubbleComponent,
    TaApprovalsTableComponent,
  ],
  templateUrl: './manage-ta-cancel-approval.component.html',
  styleUrl: './manage-ta-cancel-approval.component.scss'
})
export class ManageTaCancelApprovalComponent extends ManageTaBaseComponent {
  // Cancellation sits outside the approval chain, so the travel and acquittal steps this TA already
  // went through belong to their own pages - this table lists only the cancellation sign-off.
  readonly cancellationContexts: ApprovalContext[] = ['Cancellation'];

  // Read straight off the TA rather than the base component's estimate-first computeds - no estimate
  // is fetched for a TA in the cancellation flow (see manage-ta-container), so store.taValue would
  // be whatever the last page to request one left behind. The TA's own currentValue is the figure
  // the server derived from the itinerary versions.
  //
  // At PendingCancelApproval that is still the full value: the cancellation has only been requested,
  // and ApproveCancellationSubmission does not create the zero-value version until every approver
  // has actioned. So there is no shortfall to show yet - an original value would be identical to the
  // current one, and a "prepayment to recover" would read $0 - and this page shows the current value
  // alone. The recovery figures belong to manage-ta-cancel-finalise, after the zeroing.
  override currentTaValue = computed<number>(() => this.taSubmission()?.currentValue ?? 0);

  cancelApprovalRole = computed<'primaryApprover' | 'viewOnly'>(() => {
    if (this.permissionService.canApproveCancellation(this.taSubmission())) {
      return 'primaryApprover';
    }
    return 'viewOnly';
  });

  // Undoing a cancellation raised in error. Independent of the action above - the reverser is
  // finance/admin correcting the record, not an approver of this TA.
  canRevertCancellation = computed<boolean>(() => this.permissionService.canRevertCancellation());

  confirmReverseCancellation(): void {
    this.confirmationServiceWrapper.revertCancellationConfirm(this.taSubmission()).subscribe({
      next: () => {
        this.goBack();
      },
      error: err => {
        console.error(err);
      },
    });
  }

  confirmApproveTaCancellation(): void {
    this.confirmationServiceWrapper.approveCancellationConfirm('', this.taSubmission()).subscribe({
      next: () => {
        this.goBack();
      },
      error: err => {
        console.error(err);
      },
    });
  }
}
